import {
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { apiService, handleApiError } from "../services/api";

export default function Dashboard() {
  // Real API calls to the Flask backend
  const {
    data: systemStatus,
    error: systemError,
    isLoading: systemLoading,
  } = useQuery({
    queryKey: ["system-status"],
    queryFn: apiService.getSystemStatus,
    refetchInterval: 5000,
    retry: 2,
  });

  const {
    data: healthStatus,
    error: healthError,
    isLoading: healthLoading,
  } = useQuery({
    queryKey: ["health-status"],
    queryFn: apiService.getHealthStatus,
    refetchInterval: 10000,
    retry: 2,
  });

  const { data: analysisHistory, error: historyError } = useQuery({
    queryKey: ["analysis-history"],
    queryFn: () => apiService.getAnalysisHistory(10),
    refetchInterval: 30000,
    retry: 1,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "healthy":
        return "text-success-600 bg-success-100";
      case "warning":
        return "text-yellow-600 bg-yellow-100";
      case "error":
        return "text-danger-600 bg-danger-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getSentimentIcon = (sentiment: string | null | undefined) => {
    if (!sentiment) return null;
    switch (sentiment) {
      case "positive":
        return <ArrowTrendingUpIcon className="h-4 w-4 text-success-600" />;
      case "negative":
        return <ArrowTrendingDownIcon className="h-4 w-4 text-danger-600" />;
      default:
        return <div className="h-4 w-4 bg-gray-400 rounded-full" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Error Messages */}
      {(systemError || healthError || historyError) && (
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-danger-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-danger-800">
                Backend Connection Issues
              </h4>
              <div className="mt-2 text-sm text-danger-700">
                {systemError && (
                  <p>System Status: {handleApiError(systemError)}</p>
                )}
                {healthError && (
                  <p>Health Check: {handleApiError(healthError)}</p>
                )}
                {historyError && (
                  <p>Analysis History: {handleApiError(historyError)}</p>
                )}
                <p className="mt-1 text-xs">
                  Make sure the Flask backend is running on port 8000
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ChartBarIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">System Status</p>
              <p className="text-2xl font-semibold text-gray-900">
                {systemLoading ? (
                  <div className="spinner w-6 h-6" />
                ) : systemError ? (
                  "Error"
                ) : (
                  systemStatus?.orchestrator_status || "Unknown"
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active Tasks</p>
              <p className="text-2xl font-semibold text-gray-900">
                {systemLoading ? (
                  <div className="spinner w-6 h-6" />
                ) : systemError ? (
                  "-"
                ) : (
                  systemStatus?.active_tasks ?? 0
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ArrowTrendingUpIcon className="h-8 w-8 text-success-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Completed Today
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {systemLoading ? (
                  <div className="spinner w-6 h-6" />
                ) : systemError ? (
                  "-"
                ) : (
                  systemStatus?.completed_today ?? 0
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Health Status</p>
              <p className="text-2xl font-semibold text-gray-900">
                {healthLoading ? (
                  <div className="spinner w-6 h-6" />
                ) : healthError ? (
                  "Error"
                ) : (
                  healthStatus?.overall_status || "Unknown"
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* System Health Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            System Resources
          </h3>
          {healthStatus?.checks?.system && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">CPU Usage</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full"
                      style={{
                        width: `${healthStatus.checks.system.checks.cpu.usage_percent}%`,
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium">
                    {healthStatus.checks.system.checks.cpu.usage_percent}%
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Memory Usage</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-yellow-500 h-2 rounded-full"
                      style={{
                        width: `${healthStatus.checks.system.checks.memory.usage_percent}%`,
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium">
                    {healthStatus.checks.system.checks.memory.usage_percent}%
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Disk Usage</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-success-500 h-2 rounded-full"
                      style={{
                        width: `${healthStatus.checks.system.checks.disk.usage_percent}%`,
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium">
                    {healthStatus.checks.system.checks.disk.usage_percent}%
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Data Sources
          </h3>
          {healthStatus?.checks?.data_freshness && (
            <div className="space-y-3">
              {Object.entries(
                healthStatus.checks.data_freshness.data_sources
              ).map(([source, data]) => (
                <div key={source} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{source}</span>
                  <div className="flex items-center space-x-2">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        data.status
                      )}`}
                    >
                      {data.status}
                    </span>
                    <span className="text-xs text-gray-500">
                      {data.last_update}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Analyses */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Recent Analyses
        </h3>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Tickers</th>
                <th>Status</th>
                <th>Execution Time</th>
                <th>Sentiment</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {historyError ? (
                <tr>
                  <td colSpan={5} className="text-center py-4 text-gray-500">
                    Unable to load analysis history
                  </td>
                </tr>
              ) : !analysisHistory?.history?.length ? (
                <tr>
                  <td colSpan={5} className="text-center py-4 text-gray-500">
                    No recent analyses found
                  </td>
                </tr>
              ) : (
                analysisHistory.history.map((analysis) => (
                  <tr key={analysis.id}>
                    <td>
                      <div className="flex space-x-1">
                        {analysis.tickers.map((ticker) => (
                          <span
                            key={ticker}
                            className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800"
                          >
                            {ticker}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          analysis.status === "completed"
                            ? "bg-success-100 text-success-800"
                            : analysis.status === "running"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {analysis.status === "running" && (
                          <div className="spinner mr-1" />
                        )}
                        {analysis.status}
                      </span>
                    </td>
                    <td>
                      {analysis.execution_time
                        ? `${analysis.execution_time}s`
                        : "-"}
                    </td>
                    <td>
                      <div className="flex items-center space-x-1">
                        {getSentimentIcon(analysis.sentiment)}
                        <span className="text-sm text-gray-600">
                          {analysis.sentiment || "-"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="text-sm text-gray-500">
                        {new Date(analysis.timestamp).toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
